/**
 * Sidebar Navigation (Solid.js)
 * Desktop: fixierte Sidebar | Mobile: Off-Canvas Drawer
 */
import SeasonalAnt from './SeasonalAnt';
import SidebarEvents from './SidebarEvents';
import { SeasonalGarland } from './SeasonalBanner';
import { createSignal, createEffect, createMemo, onMount, onCleanup, Show, For } from 'solid-js';
import { Portal } from 'solid-js/web';
import { A, useLocation, useNavigate } from '@solidjs/router';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { useLanguage } from '../context/LanguageContext';
import { getNewEventCount } from '../utils/eventLogApi';
import { getHasActiveEvents } from '../utils/regionApi';
import apiClient from '../utils/api';
import { storage } from '../utils/storage';
import { syncPushSubscription } from '../utils/pushSync';

const cx = (...classes: (string | false | undefined)[]) => classes.filter(Boolean).join(' ');
const COLLAPSE_KEY = 'nav_collapsed_v1';

// Modul-level: überlebt jeden Remount der Navigation-Komponente
let _persistedScrollTop = 0;

interface NavItem {
  path: string;
  label: string;
  icon: string;
  badge?: number;
}

interface NavSection {
  id: string;
  label: string;
  icon: string;
  items: NavItem[];
}

export const SIDEBAR_WIDTH = '280px';

// M18: fr/es/pl/nl/it/pt haben nur ~30 Keys (< 50 % Abdeckung) → vorerst nicht anbieten
const LANGUAGES = [
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
];

export default function Navigation() {
  const auth = useAuth();
  const socket = useSocket();
  const langCtx = useLanguage();
  const { setLanguage, t } = langCtx;
  const location = useLocation();
  const navigate = useNavigate();
  const [langDropdownOpen, setLangDropdownOpen] = createSignal(false);
  let sidebarRef: HTMLElement | undefined;
  const [mobileOpen, setMobileOpen] = createSignal(false);
  const ALL_COLLAPSED: Record<string, boolean> = { hobby: true, education: true, activities: true, online: true, pvp: true, info: true };
  const [collapsed, setCollapsed] = createSignal<Record<string, boolean>>(
    (() => {
      try {
        const saved = JSON.parse(storage.getItem(COLLAPSE_KEY) ?? '{}');
        // Auf Mobile: alle zugeklappt wenn noch kein gespeicherter Zustand
        if (typeof window !== 'undefined' && window.innerWidth < 1024 && Object.keys(saved).length === 0) {
          return { ...ALL_COLLAPSED };
        }
        return saved;
      } catch { return {}; }
    })()
  );
  const [userDropdownOpen, setUserDropdownOpen] = createSignal(false);
  const [eventLogBadgeCount, setEventLogBadgeCount] = createSignal(0);
  const [messagesBadgeCount, setMessagesBadgeCount] = createSignal(0);
  const [friendsBadgeCount, setFriendsBadgeCount] = createSignal(0);
  const [newsBadgeCount, setNewsBadgeCount] = createSignal(0);
  const [chatBadgeCount, setChatBadgeCount] = createSignal(0);
  const [wheelBadgeCount, setWheelBadgeCount] = createSignal(0);
  const [supportBadgeCount, setSupportBadgeCount] = createSignal(0);
  const [supportAdminBadgeCount, setSupportAdminBadgeCount] = createSignal(0);
  const [currentActivities, setCurrentActivities] = createSignal<{ label: string; endMs: number; startMs: number }[]>([]);
  const [hasActiveJob, setHasActiveJob] = createSignal(false);
  const [hasActiveFoodSearch, setHasActiveFoodSearch] = createSignal(false);
  const [hasActiveQueenSearch, setHasActiveQueenSearch] = createSignal(false);
  const [activeWorkshopJobCount, setActiveWorkshopJobCount] = createSignal(0);
  const [hasActiveEvents, setHasActiveEvents] = createSignal(false);
  const [tick, setTick] = createSignal(0);

  const user = () => auth.user;

  function endMsFromCompletedAt(completedAt: string | Date | null | undefined): number | null {
    if (completedAt == null) return null;
    const ms = new Date(completedAt).getTime();
    return Number.isNaN(ms) ? null : ms;
  }

  function endMsFromStartedPlusMinutes(startedAt: string | Date | null | undefined, minutes: number): number | null {
    if (startedAt == null || minutes <= 0) return null;
    const start = new Date(startedAt).getTime();
    if (Number.isNaN(start)) return null;
    return start + minutes * 60 * 1000;
  }

  function endMsFromStartedPlusHours(startedAt: string | Date | null | undefined, hours: number): number | null {
    if (startedAt == null || hours <= 0) return null;
    const start = new Date(startedAt).getTime();
    if (Number.isNaN(start)) return null;
    return start + hours * 3600 * 1000;
  }

  function startMsFor(startedAt: string | Date | null | undefined, endMs: number, durationMs: number): number {
    if (startedAt != null) {
      const ms = new Date(startedAt).getTime();
      if (!Number.isNaN(ms)) return ms;
    }
    return endMs - durationMs;
  }

  const loadGameState = async () => {
    const uid = user()?.id;
    if (!uid) return;
    if (!apiClient.getToken()) return;
    try {
      const { data } = await apiClient.get<{ success?: boolean; gameState?: any }>(`/game/state/${uid}`);
      const g = data?.gameState;
      if (!g) {
        setCurrentActivities([]);
        return;
      }
      const now = Date.now();
      const list: { label: string; endMs: number; startMs: number }[] = [];

      if (g.activeJob) {
        const endMs = endMsFromCompletedAt(g.activeJob.completedAt) ?? endMsFromStartedPlusMinutes(g.activeJob.startedAt, 60);
        if (endMs != null && endMs > now) list.push({ label: t('navigation.activeJob'), endMs, startMs: startMsFor(g.activeJob.startedAt, endMs, 60 * 60000) });
      }
      if (g.activeResearch) {
        const hours = Number(g.activeResearch.durationHours) || 2;
        const endMs = endMsFromCompletedAt(g.activeResearch.completedAt) ?? endMsFromStartedPlusHours(g.activeResearch.startedAt, hours);
        if (endMs != null && endMs > now) list.push({ label: t('navigation.researchRunning'), endMs, startMs: startMsFor(g.activeResearch.startedAt, endMs, hours * 3600000) });
      }
      if (g.activeFoodSearch && !g.activeFoodSearch.completed) {
        const mins = Number(g.activeFoodSearch.duration_minutes) || 30;
        const endMs = endMsFromStartedPlusMinutes(g.activeFoodSearch.started_at, mins);
        if (endMs != null && endMs > now) list.push({ label: t('navigation.foodSearchRunning'), endMs, startMs: startMsFor(g.activeFoodSearch.started_at, endMs, mins * 60000) });
      }
      if (g.activeQueenSearch) {
        const endMs = g.activeQueenSearch.completedAt
          ? endMsFromCompletedAt(g.activeQueenSearch.completedAt)
          : endMsFromStartedPlusMinutes(g.activeQueenSearch.startedAt, Number(g.activeQueenSearch.durationMinutes) || 30);
        const durationMs = (Number(g.activeQueenSearch.durationMinutes) || 30) * 60000;
        if (endMs != null && endMs > now) list.push({ label: t('navigation.queenSearchRunning'), endMs, startMs: startMsFor(g.activeQueenSearch.startedAt, endMs, durationMs) });
      }
      if (g.activeQueenAddition && !g.activeQueenAddition.completedAt) {
        const mins = Number(g.activeQueenAddition.durationMinutes) || 20;
        const endMs = endMsFromStartedPlusMinutes(g.activeQueenAddition.startedAt, mins);
        if (endMs != null && endMs > now) list.push({ label: t('navigation.queenAddition'), endMs, startMs: startMsFor(g.activeQueenAddition.startedAt, endMs, mins * 60000) });
      }
      if (g.activeWorkshopJob && (g.activeWorkshopJob.status === 'active' || g.activeWorkshopJob.status === 'in_progress')) {
        const endMs = endMsFromCompletedAt(g.activeWorkshopJob.completed_at);
        if (endMs != null && endMs > now) list.push({ label: t('navigation.workshop_running'), endMs, startMs: startMsFor(g.activeWorkshopJob.started_at, endMs, 3600000) });
        setActiveWorkshopJobCount(1);
      } else if (Array.isArray(g.activeWorkshopJobs) && g.activeWorkshopJobs.length > 0) {
        const active = g.activeWorkshopJobs.filter((j: any) => j.status === 'active' || j.status === 'in_progress');
        setActiveWorkshopJobCount(active.length);
        const ends = active
          .map((j: any) => endMsFromCompletedAt(j.completed_at))
          .filter((ms: number | null): ms is number => ms != null && ms > now);
        if (ends.length > 0) {
          const endMs = Math.min(...ends);
          const first = active.find((j: any) => endMsFromCompletedAt(j.completed_at) === endMs);
          list.push({ label: t('navigation.workshop_running'), endMs, startMs: startMsFor(first?.started_at, endMs, 3600000) });
        }
      } else {
        setActiveWorkshopJobCount(0);
      }
      if (g.activeSugarWaterProduction) {
        let endMs = endMsFromCompletedAt(g.activeSugarWaterProduction.completedAt);
        const mins = g.activeSugarWaterProduction.productionType === 'mass' ? 600 : 75;
        if (endMs == null && g.activeSugarWaterProduction.startedAt) {
          endMs = endMsFromStartedPlusMinutes(g.activeSugarWaterProduction.startedAt, mins);
        }
        if (endMs != null && endMs > now) list.push({ label: t('navigation.sugarWater'), endMs, startMs: startMsFor(g.activeSugarWaterProduction.startedAt, endMs, mins * 60000) });
      }
      setCurrentActivities(list);
      setHasActiveJob(!!(g.activeJob && (endMsFromCompletedAt(g.activeJob.completedAt) ?? endMsFromStartedPlusMinutes(g.activeJob.startedAt, 60)) != null && (endMsFromCompletedAt(g.activeJob.completedAt) ?? endMsFromStartedPlusMinutes(g.activeJob.startedAt, 60))! > now));
      setHasActiveFoodSearch(!!(g.activeFoodSearch && !g.activeFoodSearch.completed));
      setHasActiveQueenSearch(!!(g.activeQueenSearch && (() => {
        const endMs = g.activeQueenSearch.completedAt
          ? endMsFromCompletedAt(g.activeQueenSearch.completedAt)
          : endMsFromStartedPlusMinutes(g.activeQueenSearch.startedAt, Number(g.activeQueenSearch.durationMinutes) || 30);
        return endMs != null && endMs > now;
      })()));
    } catch {
      setCurrentActivities([]);
      setHasActiveJob(false);
      setHasActiveFoodSearch(false);
      setHasActiveQueenSearch(false);
      setActiveWorkshopJobCount(0);
    }
  };

  createEffect(() => {
    const uid = user()?.id;
    if (!uid) {
      setCurrentActivities([]);
      return;
    }
    loadGameState();
    const interval = setInterval(loadGameState, 15000);
    // Debounce: mehrere Events in kurzer Zeit (z.B. nach Login) führen nur zu einem Fetch
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    let lastFetchTime = Date.now();
    const debouncedUpdate = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        lastFetchTime = Date.now();
        loadGameState();
      }, 500);
    };
    // Focus-Event: nur wenn seit dem letzten Fetch mind. 10 Sekunden vergangen sind
    const onFocus = () => {
      if (Date.now() - lastFetchTime >= 10000) debouncedUpdate();
    };
    window.addEventListener('active-job-changed', debouncedUpdate);
    window.addEventListener('activityUpdated', debouncedUpdate);
    window.addEventListener('gameState:invalidated', debouncedUpdate);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      if (debounceTimer) clearTimeout(debounceTimer);
      window.removeEventListener('active-job-changed', debouncedUpdate);
      window.removeEventListener('activityUpdated', debouncedUpdate);
      window.removeEventListener('gameState:invalidated', debouncedUpdate);
      window.removeEventListener('focus', onFocus);
    };
  });

  // Sekunden-Tick immer wenn eingeloggt, damit Countdown in der Sidebar live läuft
  createEffect(() => {
    if (!user()?.id) return;
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  });

  createEffect(() => {
    const uid = user()?.id;
    if (!uid) return;
    const load = async () => {
      if (!apiClient.getToken()) return;
      try {
        const count = await getNewEventCount(uid);
        setEventLogBadgeCount(count);
      } catch {
        setEventLogBadgeCount(0);
      }
    };
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  });

  createEffect(() => {
    if (!user()?.id) {
      setHasActiveEvents(false);
      return;
    }
    const load = async () => {
      if (!apiClient.getToken()) return;
      try {
        const active = await getHasActiveEvents();
        setHasActiveEvents(active);
      } catch {
        setHasActiveEvents(false);
      }
    };
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  });

  // Push-Abo abgleichen: beim Login und jedes Mal, wenn die App wieder in den Vordergrund kommt
  createEffect(() => {
    if (!user()?.id) return;
    syncPushSubscription();
    const onVisible = () => { if (document.visibilityState === 'visible') syncPushSubscription(); };
    document.addEventListener('visibilitychange', onVisible);
    onCleanup(() => document.removeEventListener('visibilitychange', onVisible));
  });

  // Glücksrad: Hinweis, solange heute noch gedreht werden kann (auch nach jedem Seitenwechsel aktualisieren)
  createEffect(() => {
    const uid = user()?.id;
    location.pathname;
    if (!uid || !apiClient.getToken()) return;
    apiClient.get<{ canSpin?: boolean; remainingSpins?: number }>('/wheel/status')
      .then((r) => setWheelBadgeCount(r.data?.canSpin ? Math.min(r.data.remainingSpins ?? 1, 2) : 0))
      .catch(() => setWheelBadgeCount(0));
  });

  createEffect(() => {
    const uid = user()?.id;
    if (!uid) return;
    const load = async () => {
      if (!apiClient.getToken()) return;
      try {
        const [msgRes, friendRes] = await Promise.all([
          apiClient.get<{ unread_count?: number }>('/messages/stats'),
          apiClient.get<{ pending_requests?: number }>('/friendships/stats'),
        ]);
        setMessagesBadgeCount(msgRes.data?.unread_count ?? 0);
        setFriendsBadgeCount(friendRes.data?.pending_requests ?? 0);
      } catch {
        setMessagesBadgeCount(0);
        setFriendsBadgeCount(0);
      }
    };
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  });

  createEffect(() => {
    const uid = user()?.id;
    const role = user()?.role;
    if (!uid || (role !== 'admin' && role !== 'supporter')) { setSupportAdminBadgeCount(0); return; }
    let interval: ReturnType<typeof setInterval>;
    let timer: ReturnType<typeof setTimeout>;
    const load = async () => {
      if (!apiClient.getToken()) return;
      try {
        const res = await apiClient.get<{ success: boolean; data?: { count: number } }>('/admin/support/unread-count');
        setSupportAdminBadgeCount(res.data?.data?.count ?? 0);
      } catch (e: any) {
        setSupportAdminBadgeCount(0);
        const st = e?.status ?? e?.response?.status;
        if (st === 401 || st === 403) clearInterval(interval); // keine Rechte: nicht weiter alle 30 s anfragen
      }
    };
    timer = setTimeout(load, 800);
    interval = setInterval(load, 30000);
    return () => { clearTimeout(timer); clearInterval(interval); };
  });

  createEffect(() => {
    const uid = user()?.id;
    if (!uid) { setSupportBadgeCount(0); return; }
    let interval: ReturnType<typeof setInterval>;
    let timer: ReturnType<typeof setTimeout>;
    const load = async () => {
      if (!apiClient.getToken()) return;
      try {
        const res = await apiClient.get<{ success: boolean; data?: { count: number } }>(`/support/tickets/unread-count/${uid}`);
        setSupportBadgeCount(res.data?.data?.count ?? 0);
      } catch (e: any) {
        setSupportBadgeCount(0);
        if (e?.status === 401 || e?.response?.status === 401) clearInterval(interval);
      }
    };
    timer = setTimeout(load, 800);
    interval = setInterval(load, 60000);
    return () => { clearTimeout(timer); clearInterval(interval); };
  });

  createEffect(() => {
    const uid = user()?.id;
    if (!uid) return;
    const loadNewsBadge = async () => {
      if (!apiClient.getToken()) return;
      try {
        const lastVisit = storage.getItem(`news_lastVisit_${uid}`) ?? undefined;
        const res = await apiClient.get<{ success: boolean; notifications?: { newNews?: number } }>(
          `/notifications/all/${uid}`,
          { params: { lastNewsVisit: lastVisit } }
        );
        if (res.data?.success) {
          setNewsBadgeCount(res.data.notifications?.newNews ?? 0);
        }
      } catch {
        setNewsBadgeCount(0);
      }
    };
    loadNewsBadge();
    const interval = setInterval(loadNewsBadge, 60000);
    // Sofort aktualisieren wenn eine neue News gepostet wurde
    const onNewsCreated = () => loadNewsBadge();
    window.addEventListener('newsCreated', onNewsCreated);
    return () => {
      clearInterval(interval);
      window.removeEventListener('newsCreated', onNewsCreated);
    };
  });

  createEffect(() => {
    const uid = user()?.id;
    if (!uid) return;
    const checkChat = async () => {
      if (!apiClient.getToken()) return;
      try {
        const res = await apiClient.get<{ id: string; user_id: string }[]>(
          '/global-chat/messages?limit=1'
        );
        const messages = Array.isArray(res.data) ? res.data : [];
        if (messages.length > 0) {
          const last = messages[0];
          const lastReadId = storage.getItem('lastReadChatMessage');
          if (last.user_id !== uid && lastReadId !== last.id) {
            setChatBadgeCount(1);
          } else {
            setChatBadgeCount(0);
          }
        } else {
          setChatBadgeCount(0);
        }
      } catch {
        setChatBadgeCount(0);
      }
    };
    checkChat();
    const interval = setInterval(checkChat, 30000);
    const onChatRead = () => setChatBadgeCount(0);
    const onChatUnread = () => checkChat();
    window.addEventListener('chatRead', onChatRead);
    window.addEventListener('chatUnread', onChatUnread);
    return () => {
      clearInterval(interval);
      window.removeEventListener('chatRead', onChatRead);
      window.removeEventListener('chatUnread', onChatUnread);
    };
  });

  createEffect(() => {
    const s = socket.socket;
    const uid = user()?.id;
    if (!s || !uid) return;
    const handleEvent = () => setEventLogBadgeCount((prev) => prev + 1);
    const handleChatMessage = (msg: { user_id?: string; id?: string }) => {
      if (msg?.user_id && msg.user_id !== uid) {
        setChatBadgeCount(1);
      }
    };
    s.on('newEvent', handleEvent);
    s.on('event-log:new', handleEvent); // so sendet das Backend (newEvent wurde nie gesendet)
    s.on('newMessage', handleChatMessage);
    s.on('chatMessage', handleChatMessage);
    return () => {
      s.off('newEvent', handleEvent);
      s.off('event-log:new', handleEvent);
      s.off('newMessage', handleChatMessage);
      s.off('chatMessage', handleChatMessage);
    };
  });

  const hasAnyActivity = () => currentActivities().length > 0;
  const primaryActivity = () => currentActivities()[0] ?? null;
  const secondaryActivity = () => currentActivities()[1] ?? null;
  const remainingMsFor = (endMs: number) => { tick(); return Math.max(0, endMs - Date.now()); };
  const timeDisplayFor = (endMs: number) => {
    const ms = remainingMsFor(endMs);
    if (ms <= 0) return '00:00';
    const s = Math.floor((ms / 1000) % 60);
    const m = Math.floor((ms / 60000) % 60);
    const h = Math.floor(ms / 3600000);
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${m}:${String(s).padStart(2, '0')}`;
  };
  const timerLabel = () => primaryActivity()?.label ?? t('navigation.ready');
  const jobTimeDisplay = () => primaryActivity() ? timeDisplayFor(primaryActivity()!.endMs) : '–';
  const progressPct = (a: { startMs: number; endMs: number }): number => {
    tick();
    const total = a.endMs - a.startMs;
    if (total <= 0) return 100;
    return Math.min(100, Math.max(0, Math.round(((Date.now() - a.startMs) / total) * 100)));
  };

  const isActive = (path?: string) => {
    if (!path) return false;
    const p = location.pathname;
    if (path === '/') return p === '/';
    return p === path || p.startsWith(`${path}/`);
  };

  const sections = createMemo((): NavSection[] => [
    {
      id: 'hobby',
      label: t('navigation.sectionAntKingdom'),
      icon: '🏠',
      items: [
        { path: '/dashboard', label: t('navigation.dashboard'), icon: '🏡' },
        { path: '/rooms', label: t('navigation.myRoom'), icon: '🛋️' },
        { path: '/workshop', label: t('navigation.workshop'), icon: '🛠️', badge: activeWorkshopJobCount() },
        { path: '/inventory', label: t('navigation.inventory'), icon: '📦' },
        { path: '/breeding', label: t('navigation.breeding'), icon: '🌱' },
      ],
    },
    {
      id: 'activities',
      label: t('navigation.sectionActivities'),
      icon: '⚡',
      items: [
        { path: '/jobs', label: t('navigation.jobs'), icon: '💼', badge: hasActiveJob() ? 1 : 0 },
        { path: '/food-search', label: t('navigation.foodSearch'), icon: '🔍', badge: hasActiveFoodSearch() ? 1 : 0 },
        { path: '/queen-search', label: t('navigation.queenSearch'), icon: '👑', badge: hasActiveQueenSearch() ? 1 : 0 },
        { path: '/research', label: t('navigation.research'), icon: '🔬' },
        { path: '/wheel-of-ants', label: t('navigation.wheelOfAnts'), icon: '🎡', badge: wheelBadgeCount() },
        { path: '/zoo-tierpark/donation', label: t('navigation.zooDonation'), icon: '🦁' },
      ],
    },
    {
      id: 'education',
      label: t('navigation.sectionEducation'),
      icon: '🎓',
      items: [
        { path: '/lexicon', label: t('navigation.lexicon'), icon: '📚' },
        { path: '/event-lexikon', label: t('navigation.eventLexikon'), icon: '🌟' },
        { path: '/tutorial', label: t('navigation.tutorial'), icon: '📖' },
      ],
    },
    {
      id: 'online',
      label: t('navigation.sectionShopping'),
      icon: '🌐',
      items: [
        { path: '/marketplace', label: t('navigation.marketplace'), icon: '🛒' },
        { path: '/shop', label: t('navigation.shop'), icon: '🏪' },
        { path: '/furniture', label: t('navigation.furniture'), icon: '🪑' },
        { path: '/housing', label: t('navigation.housing'), icon: '🏘️' },
        { path: '/customs', label: t('navigation.customs'), icon: '📜' },
      ],
    },
    ...(user()?.role === 'admin' || user()?.role === 'supporter' || user()?.role === 'beta_tester' ? [{
      id: 'pvp',
      label: t('navigation.sectionPvp'),
      icon: '⚔️',
      items: [
        { path: '/world-map', label: t('navigation.worldMap'), icon: '🌍' },
        { path: '/ant-kingdom', label: t('navigation.antKingdom'), icon: '🪹' },
      ],
    }] : []),
    {
      id: 'community',
      label: t('navigation.sectionCommunity'),
      icon: '👥',
      items: [
        { path: '/statistics', label: t('navigation.statistics'), icon: '📊' },
        { path: '/news', label: t('navigation.news'), icon: '📰', badge: newsBadgeCount() },
        { path: '/chat', label: t('navigation.chat'), icon: '💬', badge: chatBadgeCount() },
        { path: '/messages', label: t('navigation.messages'), icon: '📧', badge: messagesBadgeCount() },
        { path: '/friends', label: t('navigation.friends'), icon: '👫', badge: friendsBadgeCount() },
        { path: '/ideas', label: t('navigation.ideas'), icon: '💡' },
        { path: '/discord-settings', label: t('navigation.discord'), icon: '🔗' },
      ],
    },
    {
      id: 'info',
      label: t('navigation.sectionInfo'),
      icon: 'ℹ️',
      items: [
        { path: '/event-log', label: t('navigation.eventLog'), icon: '📋', badge: eventLogBadgeCount() },
        { path: '/hilfe', label: t('navigation.help'), icon: '❓' },
      ],
    },
  ]);

  // Body-Scroll sperren wenn Drawer offen
  createEffect(() => {
    document.body.style.overflow = mobileOpen() ? 'hidden' : '';
    onCleanup(() => { document.body.style.overflow = ''; });
  });

  onMount(() => {
    // Scroll-Position wiederherstellen
    if (sidebarRef && _persistedScrollTop > 0) sidebarRef.scrollTop = _persistedScrollTop;
    const onScroll = () => { if (sidebarRef) _persistedScrollTop = Math.round(sidebarRef.scrollTop); };
    sidebarRef?.addEventListener('scroll', onScroll, { passive: true });

    // Escape schließt Mobile-Drawer
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') { setMobileOpen(false); setUserDropdownOpen(false); } };
    document.addEventListener('keydown', onKeyDown);

    // Swipe-Geste: von links einwischen = öffnen, nach links wischen = schließen
    let swipeStartX = 0;
    let swipeStartY = 0;
    let swipeValid = false;
    const onTouchStart = (e: TouchEvent) => {
      swipeValid = e.touches.length === 1;
      if (!swipeValid) return;
      swipeStartX = e.touches[0].clientX;
      swipeStartY = e.touches[0].clientY;
    };
    const onTouchCancel = () => { swipeValid = false; };
    const onTouchEnd = (e: TouchEvent) => {
      if (!swipeValid || !e.changedTouches[0]) return;
      swipeValid = false;
      const dx = e.changedTouches[0].clientX - swipeStartX;
      const dy = Math.abs(e.changedTouches[0].clientY - swipeStartY);
      if (dy > Math.abs(dx) * 1.2) return; // vertikales Scrollen ignorieren
      if (!mobileOpen() && swipeStartX < 30 && dx > 50) setMobileOpen(true);
      else if (mobileOpen() && dx < -50) setMobileOpen(false);
    };
    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    document.addEventListener('touchcancel', onTouchCancel, { passive: true });

    // PWA: Nach Rückkehr aus dem Hintergrund / bfcache Zustand zurücksetzen,
    // damit Menü und Scroll-Sperre nie hängen bleiben
    const resetDrawer = () => { setMobileOpen(false); document.body.style.overflow = ''; };
    const onVisibility = () => { if (document.visibilityState === 'visible') resetDrawer(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', resetDrawer);

    onCleanup(() => {
      document.removeEventListener('touchcancel', onTouchCancel);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', resetDrawer);
      sidebarRef?.removeEventListener('scroll', onScroll);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchend', onTouchEnd);
    });
  });

  // Menü bei jedem Seitenwechsel schließen (auch bei Browser-Zurück)
  createEffect(() => { location.pathname; setMobileOpen(false); });

  // Aktive Sektion beim Navigieren automatisch aufklappen
  createEffect(() => {
    const path = location.pathname;
    const active = sections().find(s => s.items.some(i => path === i.path || path.startsWith(i.path + '/')));
    if (active && collapsed()[active.id]) {
      setCollapsed(prev => {
        const next = { ...prev, [active.id]: false };
        storage.setItem(COLLAPSE_KEY, JSON.stringify(next));
        return next;
      });
    }
  });

  const toggleCollapsed = (id: string) => {
    setCollapsed((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      storage.setItem(COLLAPSE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const handleLogout = () => {
    auth.logout();
    setUserDropdownOpen(false);
    setMobileOpen(false);
    navigate('/login');
  };

  const closeMobile = () => setMobileOpen(false);

  const markNewsAsRead = () => {
    const uid = user()?.id;
    if (!uid) return;
    setNewsBadgeCount(0);
    storage.setItem(`news_lastVisit_${uid}`, new Date().toISOString());
    apiClient.post(`/news/mark-as-read/${uid}`).catch(() => {});
  };

  // ── Shared render helpers ─────────────────────────────────────────────────

  const renderStatus = () => (
    <div class="sidebar-status" class:sidebar-status-inactive={!hasAnyActivity()}>
      <Show when={hasAnyActivity()} fallback={
        <div class="sidebar-status-inner">
          <div class="sidebar-status-indicator sidebar-status-indicator-inactive" />
          <div class="sidebar-status-text">
            <span class="sidebar-status-label">{t('navigation.ready')}</span>
            <span class="sidebar-status-time">–</span>
          </div>
        </div>
      }>
        <>
          <div class="sidebar-status-inner">
            <div class="sidebar-status-indicator" />
            <div class="sidebar-status-text">
              <span class="sidebar-status-label">{timerLabel()}</span>
              <span class="sidebar-status-time">{jobTimeDisplay()}</span>
            </div>
          </div>
          {/* Fortschrittsbalken für primäre Aktivität */}
          <Show when={primaryActivity()}>
            {(act) => (
              <div class="sidebar-status-progress">
                <div class="sidebar-status-progress-bar" style={{ width: `${progressPct(act())}%` }} />
              </div>
            )}
          </Show>
          <Show when={secondaryActivity()}>
            {(sec) => (
              <div class="sidebar-status-inner sidebar-status-inner-secondary">
                <div class="sidebar-status-indicator" />
                <div class="sidebar-status-text">
                  <span class="sidebar-status-label">{sec().label}</span>
                  <span class="sidebar-status-time">{timeDisplayFor(sec().endMs)}</span>
                </div>
              </div>
            )}
          </Show>
        </>
      </Show>
    </div>
  );

  const renderCurrency = (onClose?: () => void) => (
    <A href="/account-history" class="sidebar-currency sidebar-currency-link" title={t('navigation.showAccountHistory')} onClick={onClose}>
      <span class="sidebar-currency-icon">💰</span>
      <span class="sidebar-currency-amount">{Number(user()?.money ?? 0).toFixed(2)}€</span>
    </A>
  );

  const renderNav = (onClose: () => void) => (
    <nav class="sidebar-nav">
      <SidebarEvents onNavigate={onClose} />
      <For each={sections()}>
        {(section) => {
          const sectionBadgeTotal = () => section.items.reduce((sum, item) => sum + (item.badge ?? 0), 0);
          return (
          <div class="sidebar-section">
            <button class="sidebar-section-header" aria-expanded={!collapsed()[section.id]} onClick={() => toggleCollapsed(section.id)}>
              <span class="sidebar-section-label">{section.label}</span>
              <Show when={collapsed()[section.id] && sectionBadgeTotal() > 0}>
                <span class="sidebar-section-badge">{sectionBadgeTotal() > 99 ? '99+' : sectionBadgeTotal()}</span>
              </Show>
              <span class="sidebar-section-toggle" style={{ transform: collapsed()[section.id] ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease', 'font-size': '0.75rem', opacity: 0.4 }}>▼</span>
            </button>
            <Show when={!collapsed()[section.id]}>
              <div class="sidebar-section-items">
                <For each={section.items}>
                  {(item) => (
                    <A
                      href={item.path}
                      class={cx('sidebar-nav-item', isActive(item.path) && 'sidebar-nav-item-active')}
                      onClick={() => { if (item.path === '/news') markNewsAsRead(); onClose(); }}
                    >
                      <span class="sidebar-nav-item-icon">{item.icon}</span>
                      <span class="sidebar-nav-item-label">{item.label}</span>
                      <Show when={item.path === '/queen-search' && hasActiveEvents()}>
                        <span class="sidebar-nav-event-indicator" title={t('navigation.eventRegionsAvailable')}>🎪</span>
                      </Show>
                      <Show when={item.badge != null && item.badge! > 0}>
                        <span class="sidebar-nav-item-badge">{item.badge! > 99 ? '99+' : item.badge}</span>
                      </Show>
                    </A>
                  )}
                </For>
              </div>
            </Show>
          </div>
          );
        }}
      </For>
    </nav>
  );

  const renderUserSection = (onCloseDrawer: () => void) => {
    const onCloseDropdown = () => { setUserDropdownOpen(false); onCloseDrawer(); };
    return (
      <div class="sidebar-user-section">
        <button class="sidebar-user-link" aria-expanded={userDropdownOpen()} onClick={() => setUserDropdownOpen((o) => !o)}>
          <div class="sidebar-user-avatar">
            <Show when={user()?.profileImagePath} fallback={<span>{user()?.username?.[0]?.toUpperCase() ?? 'U'}</span>}>
              <img
                src={user()!.profileImagePath!.startsWith('/') ? user()!.profileImagePath! : `/images/uploads/${user()!.profileImagePath}`}
                alt={user()?.username ?? 'Avatar'}
                style={{ width: '100%', height: '100%', 'object-fit': 'cover', 'border-radius': 'inherit' }}
                onError={(e) => {
                  const t = e.currentTarget as HTMLImageElement;
                  t.style.display = 'none';
                  const parent = t.parentElement;
                  if (parent) parent.textContent = user()?.username?.[0]?.toUpperCase() ?? 'U';
                }}
              />
            </Show>
          </div>
          <div class="sidebar-user-info">
            <div class="sidebar-user-name">{user()?.username ?? 'Unbekannt'}</div>
            <div class="sidebar-user-role">
              {user()?.role === 'admin' && '👑 Admin'}
              {user()?.role === 'supporter' && '🎧 Support'}
              {user()?.role === 'beta_tester' && '🧪 Beta Tester'}
              {(!user()?.role || user()?.role === 'user') && t('navigation.player')}
            </div>
          </div>
          <span class="sidebar-user-toggle" style={{ transform: userDropdownOpen() ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease', 'font-size': '0.875rem', 'margin-left': 'auto' }}>▼</span>
        </button>
        <Show when={userDropdownOpen()}>
          <div class="sidebar-user-dropdown">
            <A href="/profile" class="sidebar-user-dropdown-item" onClick={onCloseDropdown}>
              <span class="dropdown-item-icon">👤</span>
              <div class="dropdown-item-content">
                <span class="dropdown-item-title">{t('navigation.profile')}</span>
                <span class="dropdown-item-desc">{t('navigation.editProfile')}</span>
              </div>
            </A>
            <A href="/vacation-mode" class="sidebar-user-dropdown-item" onClick={onCloseDropdown}>
              <span class="dropdown-item-icon">🏖️</span>
              <div class="dropdown-item-content">
                <span class="dropdown-item-title">{t('navigation.vacationMode')}</span>
                <span class="dropdown-item-desc">{t('navigation.protectAccount')}</span>
              </div>
            </A>
            <A href="/referral" class="sidebar-user-dropdown-item" onClick={onCloseDropdown}>
              <span class="dropdown-item-icon">🎁</span>
              <div class="dropdown-item-content">
                <span class="dropdown-item-title">{t('navigation.referral')}</span>
                <span class="dropdown-item-desc">{t('navigation.referralDesc')}</span>
              </div>
            </A>
            <A href="/support" class="sidebar-user-dropdown-item" onClick={onCloseDropdown}>
              <span class="dropdown-item-icon">🎧</span>
              <div class="dropdown-item-content">
                <span class="dropdown-item-title">{t('navigation.support')}</span>
                <span class="dropdown-item-desc">{t('navigation.createTicket')}</span>
              </div>
              <Show when={supportBadgeCount() > 0}>
                <span class="sidebar-nav-item-badge" style={{ 'margin-left': 'auto' }}>{supportBadgeCount() > 99 ? '99+' : supportBadgeCount()}</span>
              </Show>
            </A>

            {/* Language Switcher */}
            <div class="sidebar-user-dropdown-divider" />
            <div class="sidebar-lang-switcher">
              <button
                class="sidebar-user-dropdown-item"
                onClick={() => setLangDropdownOpen(o => !o)}
                style={{ width: '100%', 'text-align': 'left' }}
              >
                <span class="dropdown-item-icon">🌐</span>
                <div class="dropdown-item-content">
                  <span class="dropdown-item-title">{t('common.language')}</span>
                  <span class="dropdown-item-desc">{LANGUAGES.find(l => l.code === langCtx.language)?.label ?? langCtx.language}</span>
                </div>
                <span style={{ 'margin-left': 'auto', 'font-size': '0.75rem', opacity: 0.5, transform: langDropdownOpen() ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>▼</span>
              </button>
              <Show when={langDropdownOpen()}>
                <div style={{ 'padding': '0.25rem 0.5rem', display: 'flex', 'flex-wrap': 'wrap', gap: '0.375rem' }}>
                  <For each={LANGUAGES}>
                    {(lang) => (
                      <button
                        onClick={() => { setLanguage(lang.code); setLangDropdownOpen(false); }}
                        style={{
                          padding: '0.3rem 0.625rem',
                          'border-radius': '0.5rem',
                          border: langCtx.language === lang.code ? '1px solid rgba(6,182,212,0.6)' : '1px solid rgba(148,163,184,0.2)',
                          background: langCtx.language === lang.code ? 'rgba(6,182,212,0.15)' : 'rgba(30,41,59,0.5)',
                          color: langCtx.language === lang.code ? '#22d3ee' : '#94a3b8',
                          'font-size': '0.8125rem',
                          cursor: 'pointer',
                          'font-weight': langCtx.language === lang.code ? '700' : '400',
                        }}
                      >
                        {lang.flag} {lang.label}
                      </button>
                    )}
                  </For>
                </div>
              </Show>
            </div>

            <div class="sidebar-user-dropdown-divider" />
            <Show when={user()?.role === 'admin'}>
              <A href="/admin" class="sidebar-user-dropdown-item sidebar-user-dropdown-item-admin" onClick={onCloseDropdown}>
                <span class="dropdown-item-icon">⚙️</span>
                <div class="dropdown-item-content">
                  <span class="dropdown-item-title">{t('navigation.admin')}</span>
                  <span class="dropdown-item-desc">{t('navigation.manageGame')}</span>
                </div>
              </A>
            </Show>
            <Show when={user()?.role === 'admin' || user()?.role === 'supporter'}>
              <A href="/support-admin" class="sidebar-user-dropdown-item sidebar-user-dropdown-item-admin" onClick={onCloseDropdown}>
                <span class="dropdown-item-icon">🎧</span>
                <div class="dropdown-item-content">
                  <span class="dropdown-item-title">{t('navigation.supportAdmin')}</span>
                  <span class="dropdown-item-desc">{t('navigation.manageTickets')}</span>
                </div>
                <Show when={supportAdminBadgeCount() > 0}>
                  <span class="sidebar-nav-item-badge" style={{ 'margin-left': 'auto' }}>{supportAdminBadgeCount() > 99 ? '99+' : supportAdminBadgeCount()}</span>
                </Show>
              </A>
            </Show>
            <Show when={user()?.role === 'admin' || user()?.role === 'supporter'}>
              <div class="sidebar-user-dropdown-divider" />
            </Show>
            <button class="sidebar-user-dropdown-item sidebar-user-dropdown-item-logout" onClick={handleLogout}>
              <span class="dropdown-item-icon">🚪</span>
              <div class="dropdown-item-content">
                <span class="dropdown-item-title">{t('auth.logout')}</span>
                <span class="dropdown-item-desc">{t('navigation.endSession')}</span>
              </div>
            </button>
          </div>
        </Show>
      </div>
    );
  };

  return (
    <>
      <div class="sidebar-wrapper">
        <aside class="sidebar" aria-label="Seitennavigation" ref={sidebarRef}>
          <div class="sidebar-logo">
            <A href="/dashboard" class="sidebar-logo-link" onClick={closeMobile}>
              <span class="sidebar-logo-icon"><SeasonalAnt /></span>
              <span class="sidebar-logo-text">King of Ants</span>
            </A>
            <SeasonalGarland />
          </div>
          {renderCurrency()}
          {renderStatus()}
          {renderNav(closeMobile)}
          {renderUserSection(closeMobile)}
        </aside>
      </div>

      <Portal>
        <div class="mobile-bar">
          <button class="mobile-hamburger" aria-label={mobileOpen() ? t('navigation.mobileMenuClose') : t('navigation.mobileMenuOpen')} aria-expanded={mobileOpen()} onClick={() => setMobileOpen((o) => !o)}>
            {mobileOpen() ? '✕' : '☰'}
          </button>
          <A href="/dashboard" class="mobile-title" onClick={closeMobile}>
            <span><SeasonalAnt /></span> King of Ants
          </A>
        </div>

        <Show when={mobileOpen()}>
          <div class="mobile-overlay" onClick={closeMobile} />
        </Show>

        <aside
          class={cx('mobile-drawer', mobileOpen() && 'mobile-drawer-open')}
          aria-label="Mobile Navigation"
          aria-hidden={!mobileOpen()}
          inert={!mobileOpen()}
        >
          <div class="sidebar-logo">
            <A href="/dashboard" class="sidebar-logo-link" onClick={closeMobile}>
              <span class="sidebar-logo-icon"><SeasonalAnt /></span>
              <span class="sidebar-logo-text">King of Ants</span>
            </A>
            <SeasonalGarland />
          </div>
          {renderStatus()}
          {renderCurrency(closeMobile)}
          {renderNav(closeMobile)}
          {renderUserSection(closeMobile)}
        </aside>
      </Portal>
    </>
  );
}
